/**
 * ExportPdfModal.js - Modal para seleccionar entradas y exportar expediente a PDF
 * Incluye opción para agregar lugares de confianza
 */
import React, { useState, useMemo, useEffect } from 'react';
import { getFavorites } from '../localizador/services/favoritesService';

export default function ExportPdfModal({
  show,
  onClose,
  entradas,
  onExport,
  loading,
  lugaresGuardados = [] // Lugares ya guardados en el expediente compartido
}) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [incluirDocumentos, setIncluirDocumentos] = useState(true);

  // Estados para lugares de confianza
  const [incluirLugares, setIncluirLugares] = useState(lugaresGuardados.length > 0);
  const [lugaresSeleccionados, setLugaresSeleccionados] = useState([]);
  const [favoritosDisponibles, setFavoritosDisponibles] = useState([]);
  const [loadingFavoritos, setLoadingFavoritos] = useState(false);

  // Ordenar entradas por fecha (más reciente primero)
  const entradasOrdenadas = useMemo(() => {
    return [...entradas].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [entradas]);

  // Cargar favoritos cuando se activa el checkbox
  useEffect(() => {
    async function cargarFavoritos() {
      if (!incluirLugares || favoritosDisponibles.length > 0) return;

      setLoadingFavoritos(true);
      try {
        const favoritos = await getFavorites();
        const favoritosConLugar = favoritos.filter(f => f.placeData || f.lugar);
        setFavoritosDisponibles(favoritosConLugar);

        // Si hay lugares guardados, pre-seleccionarlos
        if (lugaresGuardados.length > 0) {
          setLugaresSeleccionados(lugaresGuardados.map((l, idx) => ({
            id: `saved-${idx}`,
            ...l
          })));
        }
      } catch (err) {
        console.error('Error cargando favoritos:', err);
      } finally {
        setLoadingFavoritos(false);
      }
    }

    if (show && incluirLugares) {
      cargarFavoritos();
    }
  }, [show, incluirLugares, lugaresGuardados, favoritosDisponibles.length]);

  if (!show) return null;

  // Contar documentos de una entrada
  const contarDocumentos = (entrada) => {
    let count = 0;
    if (entrada.recetaPath || entrada.recetaUrl) count++;
    if (entrada.attachmentPath || entrada.attachmentUrl) count++;
    if (entrada.documentoAdicionalPath || entrada.documentoAdicionalUrl) count++;
    return count;
  };

  // Toggle selección de una entrada
  const toggleEntrada = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Seleccionar todo
  const seleccionarTodo = () => {
    setSelectedIds(new Set(entradasOrdenadas.map(e => e.id)));
  };

  // Limpiar selección
  const limpiarSeleccion = () => {
    setSelectedIds(new Set());
  };

  // Manejar toggle de lugar individual
  const handleToggleLugar = (favorito) => {
    const lugarData = favorito.placeData || favorito.lugar || favorito;
    const lugarId = favorito.id;

    setLugaresSeleccionados(prev => {
      const yaSeleccionado = prev.some(l => l.id === lugarId);

      if (yaSeleccionado) {
        return prev.filter(l => l.id !== lugarId);
      } else {
        if (prev.length >= 4) {
          return prev; // Límite de 4
        }
        return [...prev, {
          id: lugarId,
          nombre: lugarData.nombre || favorito.nombre || 'Sin nombre',
          categoria: lugarData.categoria || favorito.categoria || '',
          telefono: lugarData.telefono || lugarData.phone || '',
          direccion: lugarData.direccion || lugarData.address || ''
        }];
      }
    });
  };

  // Obtener icono de categoría
  const getIconoCategoria = (categoria) => {
    const iconos = {
      'veterinaria': '🏥',
      'farmacia': '💊',
      'pension': '🏠',
      'estilistas': '✂️',
      'parques': '🌳',
      'tiendas': '🏪',
      'cerrajeria': '🔑',
      'mecanico': '🔧',
      'conveniencia': '🏪'
    };
    return iconos[(categoria || '').toLowerCase()] || '📍';
  };

  // Manejar exportación
  const handleExport = () => {
    if (selectedIds.size === 0) return;
    const lugaresParaPdf = incluirLugares ? lugaresSeleccionados : [];
    onExport(Array.from(selectedIds), incluirDocumentos, lugaresParaPdf);
  };

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';
    return new Date(fecha).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100000,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          borderRadius: '16px',
          maxWidth: '550px',
          width: '100%',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="modal-header-gradient d-flex justify-content-between align-items-center"
          style={{ padding: '16px 20px' }}
        >
          <h5
            className="mb-0"
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'white'
            }}
          >
            <i className="bi bi-file-earmark-pdf me-2"></i>
            Exportar Expediente a PDF
          </h5>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={onClose}
            disabled={loading}
          ></button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '20px',
            overflowY: 'auto',
            flex: 1
          }}
        >
          {/* Instrucciones */}
          <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
            Selecciona las entradas que deseas incluir en el PDF:
          </p>

          {/* Botones de selección rápida */}
          <div className="d-flex gap-2 mb-3">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={seleccionarTodo}
              disabled={loading}
            >
              Seleccionar todo
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={limpiarSeleccion}
              disabled={loading || selectedIds.size === 0}
            >
              Limpiar seleccion
            </button>
          </div>

          {/* Lista de entradas */}
          <div
            style={{
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: '8px'
            }}
          >
            {entradasOrdenadas.length === 0 ? (
              <div className="text-center text-muted p-4">
                No hay entradas en el expediente
              </div>
            ) : (
              entradasOrdenadas.map((entrada) => {
                const numDocs = contarDocumentos(entrada);
                const isSelected = selectedIds.has(entrada.id);

                return (
                  <div
                    key={entrada.id}
                    className={`p-3 border-bottom ${isSelected ? 'bg-light' : ''}`}
                    style={{
                      cursor: 'pointer',
                      transition: 'background-color 0.15s'
                    }}
                    onClick={() => toggleEntrada(entrada.id)}
                  >
                    <div className="d-flex align-items-start">
                      <input
                        type="checkbox"
                        className="form-check-input me-3 mt-1"
                        checked={isSelected}
                        onChange={() => {}}
                        style={{ cursor: 'pointer' }}
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong style={{ fontSize: '14px' }}>
                              {formatearFecha(entrada.fecha)} - {entrada.tipo || 'Sin tipo'}
                            </strong>
                          </div>
                        </div>

                        {/* Datos resumidos */}
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {entrada.temperatura && (
                            <span className="me-2">Temp: {entrada.temperatura}°C</span>
                          )}
                          {entrada.peso && (
                            <span>Peso: {entrada.peso}kg</span>
                          )}
                        </div>

                        {/* Notas truncadas */}
                        {entrada.notas && (
                          <div
                            style={{
                              fontSize: '12px',
                              color: '#888',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%'
                            }}
                          >
                            {entrada.notas.substring(0, 80)}{entrada.notas.length > 80 ? '...' : ''}
                          </div>
                        )}

                        {/* Documentos */}
                        <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                          {numDocs > 0 ? (
                            <span>
                              <i className="bi bi-paperclip me-1"></i>
                              {numDocs} documento{numDocs > 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span>Sin documentos</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Opción de incluir documentos */}
          <div className="form-check mt-3">
            <input
              type="checkbox"
              className="form-check-input"
              id="incluirDocumentos"
              checked={incluirDocumentos}
              onChange={(e) => setIncluirDocumentos(e.target.checked)}
              disabled={loading}
            />
            <label
              className="form-check-label"
              htmlFor="incluirDocumentos"
              style={{ fontSize: '14px' }}
            >
              Incluir lista de documentos PDF anexos al final
            </label>
          </div>

          {/* Sección de Lugares de Confianza */}
          <div className="mt-4 pt-3 border-top">
            <div className="form-check mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="incluirLugares"
                checked={incluirLugares}
                onChange={(e) => {
                  setIncluirLugares(e.target.checked);
                  if (!e.target.checked) {
                    setLugaresSeleccionados([]);
                  }
                }}
                disabled={loading}
              />
              <label
                className="form-check-label"
                htmlFor="incluirLugares"
                style={{ fontSize: '14px', fontWeight: 500 }}
              >
                <i className="bi bi-geo-alt me-1 text-success"></i>
                Incluir lugares de confianza
              </label>
            </div>

            {incluirLugares && (
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  maxHeight: '150px',
                  overflowY: 'auto'
                }}
              >
                {loadingFavoritos ? (
                  <div className="text-center py-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="mb-0 mt-1 text-muted" style={{ fontSize: '12px' }}>
                      Cargando favoritos...
                    </p>
                  </div>
                ) : favoritosDisponibles.length === 0 ? (
                  <div className="text-center py-2">
                    <i className="bi bi-star text-muted"></i>
                    <p className="mb-0 mt-1 text-muted" style={{ fontSize: '12px' }}>
                      No tienes favoritos. Busca servicios en el Localizador y guardalos.
                    </p>
                  </div>
                ) : (
                  <>
                    {favoritosDisponibles.map((favorito) => {
                      const lugarData = favorito.placeData || favorito.lugar || {};
                      const nombre = lugarData.nombre || favorito.nombre || 'Sin nombre';
                      const categoria = lugarData.categoria || favorito.categoria || '';
                      const telefono = lugarData.telefono || lugarData.phone || '';
                      const isSelected = lugaresSeleccionados.some(l => l.id === favorito.id);

                      return (
                        <div
                          key={favorito.id}
                          className="form-check mb-2"
                          style={{
                            padding: '6px 8px',
                            borderRadius: '4px',
                            backgroundColor: isSelected ? '#dcfce7' : 'transparent',
                            border: isSelected ? '1px solid #86efac' : '1px solid transparent'
                          }}
                        >
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`pdf-lugar-${favorito.id}`}
                            checked={isSelected}
                            onChange={() => handleToggleLugar(favorito)}
                            disabled={!isSelected && lugaresSeleccionados.length >= 4}
                          />
                          <label
                            className="form-check-label d-flex align-items-center"
                            htmlFor={`pdf-lugar-${favorito.id}`}
                            style={{ fontSize: '13px', cursor: 'pointer' }}
                          >
                            <span style={{ marginRight: '6px' }}>
                              {getIconoCategoria(categoria)}
                            </span>
                            <div>
                              <div style={{ fontWeight: 500 }}>{nombre}</div>
                              {telefono && (
                                <div style={{ fontSize: '11px', color: '#6b7280' }}>{telefono}</div>
                              )}
                            </div>
                          </label>
                        </div>
                      );
                    })}
                    <div className="text-end mt-2" style={{ fontSize: '11px', color: '#6b7280' }}>
                      {lugaresSeleccionados.length}/4 seleccionados
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Contador */}
          <div
            className="mt-3 p-2 bg-light rounded text-center"
            style={{ fontSize: '14px' }}
          >
            <strong>Total seleccionado:</strong> {selectedIds.size} entrada{selectedIds.size !== 1 ? 's' : ''}
            {incluirLugares && lugaresSeleccionados.length > 0 && (
              <span className="ms-2">
                + {lugaresSeleccionados.length} lugar{lugaresSeleccionados.length !== 1 ? 'es' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="d-flex justify-content-end gap-2"
          style={{
            padding: '16px 20px',
            borderTop: '1px solid #dee2e6'
          }}
        >
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleExport}
            disabled={loading || selectedIds.size === 0}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Generando...
              </>
            ) : (
              <>
                <i className="bi bi-file-earmark-pdf me-2"></i>
                Generar PDF
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
